/* eslint-disable react/no-array-index-key */
import { IReview } from "api/interfaces/home/home.type";
import StarRatings from "component/design-system/StarRatings";
import PAButton, { BUTTON_TYPE } from "component/design-system/buttons/PAButton";
import PAImage from "component/design-system/images/PAImage";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import ReadMore from "component/read-more/ReadMore";
import { RootState } from "interfaces/generics.type";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { useSelector } from "react-redux";
import { dateToYMD } from "util/date-helper";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import { numberFormat } from "util/helper/numberFormat";
import styles from "./customer-review-v2.module.scss";

const INITIAL_COLORS = ["#3b82f6", "#a855f7", "#14b8a6"];

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/);
  return parts.map((p) => p[0]?.toUpperCase()).join("").slice(0, 2);
}

function ReviewCard({ review, colorIndex }: { review: IReview; colorIndex: number }) {
  const initials = getInitials(review.name);
  const color = INITIAL_COLORS[colorIndex];

  return (
    <div className={styles.review_card}>
      <div className={styles.review_card_left}>
        <div className={styles.initials_circle} style={{ backgroundColor: color }}>
          {initials}
        </div>
        <div className={styles.review_info}>
          <PAText component="span" bold>{review.name}</PAText>
          <div className="d-flex justify-content-between">
            <PAText component="span" fontSize={FONT_SIZE[6]} color="light-grey">
              {dateToYMD(new Date(review.updateTime))}
            </PAText>
            <StarRatings rating={review.rating} className={styles.star_rating} />
          </div>
          <ReadMore
            text={(
              <PAText
                component="span"
                fontSize={FONT_SIZE[1]}
                color="medium-grey"
              >
                {`"${review.review}"`}
              </PAText>
            )}
            lineClamp={3}
          />
        </div>
      </div>
    </div>
  );
}

interface ICustomerReviewV2Props {
  reviews: IReview[];
}

function CustomerReviewV2(props: ICustomerReviewV2Props) {
  const {
    reviews,
  } = props;
  const { t } = useTranslation("home");
  const { averageRating, totalReviewCount } = useSelector((store: RootState) => store.authenticationState);

  if (!reviews || !reviews.length) {
    return null;
  }

  const top3Reviews = reviews.slice(0, 3);

  return (
    <div className={styles.customer_review_v2_container}>
      <PAText
        bold
        component="h2"
        className="d-flex align-items-center justify-content-center m-b-15"
        style={{ width: "100%", marginTop: 0 }}
        fontSize={FONT_SIZE[5]}
      >
        {t("Customers Love Parts Avatar")}
      </PAText>
      <PAText component="p" fontSize={FONT_SIZE[1]} color="medium-grey" className={styles.customer_review_v2_subtext}>
        {t("Join over 400,000+ verified customers")}
      </PAText>

      <div className={styles.google_summary_card}>
        <div className={styles.google_left}>
          <div>
            <PAImage
              width={40}
              height={40}
              alt="Google"
              src={getRelativeImagePath("/cms/pubicImages/images/google-logo-v2.webp")}
            />
            <PAText color="light-grey" fontSize={FONT_SIZE[0]}>Google</PAText>
          </div>
          <div className={styles.google_rating_info}>
            <PAText component="span" fontSize={FONT_SIZE[5]} bold>{averageRating}</PAText>
            <PAText component="span" fontSize={FONT_SIZE[6]} color="medium-grey">{t("out of 5")}</PAText>
            <div className={styles.google_stars}>
              <StarRatings rating={Number(averageRating) || 4.6} />
            </div>
          </div>

        </div>
        <div className={styles.google_right}>
          <PAText component="span" fontSize={FONT_SIZE[5]} bold>
            {`${numberFormat(totalReviewCount)}+`}
          </PAText>
          <PAText component="span" fontSize={FONT_SIZE[6]} color="light-grey">{t("Reviews")}</PAText>
        </div>
      </div>

      <div className={styles.reviews_list}>
        {top3Reviews.map((review, index) => (
          <ReviewCard key={review.reviewId} review={review} colorIndex={index} />
        ))}
      </div>
      <div className={styles.read_all_container}>
        <Link
          href="https://search.google.com/local/reviews?placeid=ChIJV_3gXV7S1IkRnZujM9GnVDo"
          target="_blank"
        >
          <PAButton buttonType={BUTTON_TYPE.PRIMARY} widthAuto className={styles.read_all_reviews_button}>

            {t("Read All Reviews")}

          </PAButton>
        </Link>
      </div>
    </div>
  );
}

export default CustomerReviewV2;
